<template>
	<div class="ar_current_overlay" v-if="visible" @click.self="close">
		<div class="ar_current_box">
			<div class="ar_current_head">
				<strong>A/R Summary by Customer — Current (만기 미도래 기준)</strong>
				<a href="javascript:;" class="ar_current_close" @click="close">×</a>
			</div>
			<div class="ar_current_tools">
				<span>기준일</span>
				<CustomDatePicker v-model="as_of" />
				<a href="javascript:;" class="search_bt" @click="load">조회</a>
				<a href="javascript:;" class="gray_bt" @click="exportExcel">Excel</a>
				<a href="javascript:;" class="gray_bt" @click="sheet">SBM Sheet</a>
				<span class="ar_current_note">* Current = 만기(invoice date + payment term 일수) 미도래 잔액. PT 일수는 코드에서 추론(N##/PDC## 등), [Basic Info &gt; Code]에서 정의 시 정밀해짐.</span>
			</div>
			<div class="ar_current_table">
				<table class="list_tbl">
					<thead>
						<tr>
							<th style="width:12%;">Cust #</th>
							<th style="width:23%;">Name</th>
							<th style="width:11%;">Current</th>
							<th style="width:11%;">1-30 Days</th>
							<th style="width:11%;">31-60 Days</th>
							<th style="width:11%;">61-90 Days</th>
							<th style="width:11%;">Over 90 Days</th>
							<th style="width:10%;">Total</th>
						</tr>
					</thead>
					<tbody>
						<tr class="ar_current_total">
							<td class="al_center" colspan="2">ALL ({{ list.length }})</td>
							<td class="al_right">{{ totals.current_amt | toCurrency }}</td>
							<td class="al_right">{{ totals.d1_30 | toCurrency }}</td>
							<td class="al_right">{{ totals.d31_60 | toCurrency }}</td>
							<td class="al_right">{{ totals.d61_90 | toCurrency }}</td>
							<td class="al_right">{{ totals.over_90 | toCurrency }}</td>
							<td class="al_right">{{ totals.total | toCurrency }}</td>
						</tr>
						<tr v-for="(r, i) in list" :key="i">
							<td>{{ r.cust_no }}</td>
							<td class="al_left">{{ r.name }}</td>
							<td class="al_right">{{ r.current_amt | toCurrency }}</td>
							<td class="al_right">{{ r.d1_30 | toCurrency }}</td>
							<td class="al_right">{{ r.d31_60 | toCurrency }}</td>
							<td class="al_right">{{ r.d61_90 | toCurrency }}</td>
							<td class="al_right">{{ r.over_90 | toCurrency }}</td>
							<td class="al_right">{{ r.total | toCurrency }}</td>
						</tr>
						<tr v-if="!list.length">
							<td colspan="8" class="al_center">No data</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
		<!-- #3312 이 모달의 overlay(z-index 1000) 위에 보이도록 SheetCopyPopup(Modal z-index 30)을 올린다 -->
		<SheetCopyPopup v-if="sheet_popup" :sheet="sheet_popup" style="z-index:3000" @close="sheet_popup = null" />
	</div>
</template>

<script>
import CustomDatePicker from '@/components/common/CustomDatePicker.vue';
import SheetCopyPopup from '@/components/common/SheetCopyPopup';
import { apiArCurrentSummary } from '@/api/report/report';
import { exportDataExcel } from '@/api/util';
import { defaultDataType } from 'export-xlsx';
import { sumColumn } from '@/utils/sheetCopy';
import { today } from '@/utils/date.js';

export default {
	name: 'ArCurrentSummaryModal',
	components: { CustomDatePicker, SheetCopyPopup },
	props: {
		visible: { type: Boolean, default: false },
	},
	data() {
		return {
			as_of: today(),
			list: [],
			sheet_popup: null,
		};
	},
	computed: {
		totals() {
			const t = { current_amt: 0, d1_30: 0, d31_60: 0, d61_90: 0, over_90: 0, total: 0 };
			this.list.forEach(r => {
				Object.keys(t).forEach(k => (t[k] += Number(r[k]) || 0));
			});
			return t;
		},
	},
	watch: {
		visible(v) {
			if (v) this.load();
		},
	},
	methods: {
		close() {
			this.$emit('close');
		},
		async load() {
			const { data } = await apiArCurrentSummary(this.as_of);
			this.list = data && Array.isArray(data.list) ? data.list : [];
		},
		exportExcel() {
			const header = [
				{ key: 'cust_no', name: 'Cust #', width: 14 },
				{ key: 'name', name: 'Name', width: 36 },
				{ key: 'current_amt', name: 'Current', width: 14 },
				{ key: 'd1_30', name: '1-30 Days', width: 14 },
				{ key: 'd31_60', name: '31-60 Days', width: 14 },
				{ key: 'd61_90', name: '61-90 Days', width: 14 },
				{ key: 'over_90', name: 'Over 90 Days', width: 14 },
				{ key: 'total', name: 'Total', width: 14 },
			];
			const all = Object.assign({ cust_no: 'ALL', name: '(' + this.list.length + ')' }, this.totals);
			exportDataExcel([all].concat(this.list), header, 'AR_Summary_Current');
		},
		// #3312 SBM 양식 Sheet: 화면 8열 + '# of Transactions'(transaction_count). 화면 표·Excel 은 그대로 둔다.
		sheetHeaders() {
			return [
				{ key: 'cust_no', name: 'Cust #', dataType: defaultDataType.string },
				{ key: 'name', name: 'Name', dataType: defaultDataType.string },
				{ key: 'current_amt', name: 'Current', dataType: defaultDataType.currency },
				{ key: 'd1_30', name: '1-30 Days', dataType: defaultDataType.currency },
				{ key: 'd31_60', name: '31-60 Days', dataType: defaultDataType.currency },
				{ key: 'd61_90', name: '61-90 Days', dataType: defaultDataType.currency },
				{ key: 'over_90', name: 'Over 90 Days', dataType: defaultDataType.currency },
				{ key: 'total', name: 'Total', dataType: defaultDataType.currency },
				{ key: 'transaction_count', name: '# of Transactions', dataType: defaultDataType.number },
			];
		},
		// 빈 열 1개를 앞에 두고 헤더 바로 아래에 합계 행을 넣는다. 라벨은 첨부 문자 그대로 'ALL ()' 이고 Name 열은 공란이다.
		// 금액 합은 화면 totals(부동소수 누적, toCurrency 로만 가려짐)가 아니라 sumColumn 으로 소수 2자리 반올림해 1405127.3599999996 같은 값이 복사되지 않게 한다.
		sheet() {
			if (!this.list.length) {
				this.$toast.error('Please search first');
				return false;
			}
			const total_row = { cust_no: 'ALL ()' };
			['current_amt', 'd1_30', 'd31_60', 'd61_90', 'over_90', 'total'].forEach(key => (total_row[key] = sumColumn(this.list, key)));
			total_row.transaction_count = sumColumn(this.list, 'transaction_count', 0);
			this.sheet_popup = {
				title: 'A/R Summary (SBM Sheet)',
				headers: this.sheetHeaders(),
				rows: this.list,
				options: { leading_blank: true, total_row },
			};
		},
	},
};
</script>

<style scoped>
.ar_current_overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.4);
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
}
.ar_current_box {
	background: #fff;
	width: 92%;
	max-width: 1120px;
	max-height: 88vh;
	display: flex;
	flex-direction: column;
	border-radius: 4px;
}
.ar_current_head {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px 14px;
	border-bottom: 1px solid #ddd;
}
.ar_current_close {
	font-size: 20px;
	line-height: 1;
}
.ar_current_tools {
	padding: 8px 14px;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}
.ar_current_note {
	color: #888;
	font-size: 12px;
}
.ar_current_table {
	overflow: auto;
	padding: 0 14px 14px;
}
.ar_current_total td {
	font-weight: bold;
	background: #f4f6f8;
}
</style>
